import { config } from '@/config';
import type MixpanelEvent from '@/integrations/Mixpanel/MixpanelEvent';
import type { MixpanelClientType } from '@/integrations/Mixpanel/types';
import { useKycSessionStore } from '@/stores/kycSession.store';
import { getCorrelationIdFromRouter } from '@/util/routerUtil';
import mixpanel from 'mixpanel-browser';

export default class MixpanelClient implements MixpanelClientType {
  private isInitialised = false;

  private get token(): string {
    return config.testModeEnabled ? config.mixpanelTokenTest : config.mixpanelTokenLive;
  }

  private get apiHost(): string {
    return config.apiBaseUrl + '/mixpanel';
  }

  /**
   * For events attempted to be tracked before mixpanel initialisation
   */
  private queue: Set<MixpanelEvent> = new Set();

  init() {
    if (!this.token) {
      return;
    }
    const correlationId = getCorrelationIdFromRouter();
    const kycStore = useKycSessionStore();
    const accountId = kycStore.kycSession?.accountId;
    mixpanel.init(this.token, {
      debug: config.env === 'local',
      track_pageview: false,
      persistence: 'localStorage',
      persistence_name: `session_mp_persistence_${correlationId}`,
      api_host: this.apiHost,
    });
    this.isInitialised = true;
    this.identify(accountId);
    this.flush();
    window.addEventListener('beforeunload', () => {
      this.reset();
    });
  }

  private identifier: string | undefined;

  identify(accountId?: string) {
    if (!this.isInitialised) {
      return;
    }
    if (this.identifier === accountId) {
      // skipping identification account id not changed
      return;
    }
    this.identifier = accountId;
    mixpanel.identify(accountId);
    this.flush();
  }

  reset() {
    if (!this.isInitialised) {
      return;
    }
    mixpanel.reset();
  }

  private _track(event: MixpanelEvent, sendImmediately = false) {
    mixpanel.track(event.name, event.properties, { send_immediately: sendImmediately });
  }

  track(event: MixpanelEvent): void {
    if (!this.isInitialised) {
      this.queue.add(event);
      return;
    }
    this._track(event);
  }

  private flush() {
    if (!this.isInitialised) {
      return;
    }
    this.queue.forEach((event) => {
      this._track(event, false);
      this.queue.delete(event);
    });
  }
}
